import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import AxiosLandingHelmet from 'common/helmets/AxiosLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import AxiosWordmark from 'img/landing/axios-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const AxiosLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Axios Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAxios landingCustomersPage">
      <AxiosLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Axios uses Canny to keep internal teams aligned, inform stakeholders, and reduce time spent on messy communication" />
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              There is a lot of information out there. Media stories are often too long or
              confusing. Websites are a&nbsp;mess.
            </p>
            <p>
              It can be hard for people to keep up with world news without getting lost or
              overwhelmed. Axios, a news media company, aims to change&nbsp;that.
            </p>
            <p className="quote">
              &ldquo;Our mission is to deliver the important news and information you need
              efficiently. That’s what our audience comes to us&nbsp;for.&rdquo;
            </p>
            <p>
              Marcus Moretti joined Axios in February 2019. He was assigned to lead the product
              development of the Axios app—an even more mobile-friendly way to get up to speed on
              the consequential topics shaping our&nbsp;future.
            </p>
            <p>
              Since then, the Axios app has moved from an internal product, to a public beta, to a
              fully functional public&nbsp;release.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Marcus knew he wanted to try a user feedback solution before he even
              joined&nbsp;Axios.
            </p>
            <p className="quote">
              &ldquo;I realized [Canny] could solve a lot of problems that I had experienced in past
              roles. I didn’t have these issues at Axios yet, but I knew I might if we didn’t get a
              hold of it right&nbsp;away.&rdquo;
            </p>
            <p>
              He was also skeptical about some of the other feedback tools he’d seen before, and
              knew exactly what he&nbsp;wanted:
            </p>
            <p className="quote">
              &ldquo;We just needed a simple interface that lets users post their feedback that we
              can then organize and&nbsp;reference.&rdquo;
            </p>
            <p>
              By using a dedicated user feedback tool, Marcus wanted to achieve the
              following&nbsp;goals:
            </p>
            <ul>
              <li>
                Get all information together into one tool—a single source of user&nbsp;feedback
              </li>
              <li>Reduce time spent on internal communication across several&nbsp;channels</li>
              <li>
                Give users an easily accessible place to leave feedback, with an easy to
                use&nbsp;interface
              </li>
            </ul>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Marcus had come across Canny as a user before on another product, and was impressed
              with the ease of&nbsp;use.
            </p>
            <p>
              When the internal alpha version of the Axios app was released, the team opened up
              feedback in the product, via a link to a private Canny board. Testers immediately
              started using&nbsp;it.
            </p>
            <p className="quote">
              &ldquo;It was easy for the user who’s posting feedback as well as the admin. My
              colleagues posted and upvoted in the app&nbsp;frequently.&rdquo;
            </p>
            <p>
              Besides Canny being immediately accepted by testers, internal communication also
              became a lot&nbsp;easier.
            </p>
            <p className="quote">
              &ldquo;Instead of dozens of calls, emails, and instant messages, the team was able to
              efficiently communicate and collaborate in one&nbsp;place.&rdquo;
            </p>
            <p>
              Internally, the Canny board served as an easily accessible source of truth that aided
              plenty of&nbsp;conversations.
            </p>
            <p>
              When talking to various stakeholders, Marcus would pull up the Canny board to help
              them understand what the users’ main requests&nbsp;were.
            </p>
            <p className="quote">
              &ldquo;Canny made the most important and urgent user feedback stand out, so we could
              innovate our product and deliver an even better experience for
              our&nbsp;audience.&rdquo;
            </p>
            <p>
              Axios internal team's response to Canny was promising. The engagement from testers
              clearly indicated that Canny could serve as a single source of truth for all
              user&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;It was as useful as I thought it was going to be, actually more so. We decided
              to go ahead and also use it for the next phase: a closed&nbsp;beta.&rdquo;
            </p>
            <p>
              They created a different—public—board. The night they released the app to about a
              thousand beta testers, they already had a large number of posts and&nbsp;upvotes.
            </p>
            <p>
              Since then, Axios has launched{' '}
              <a href="http://axios.com/app" rel="noopener" target="_blank">
                the app
              </a>
              , and they are continuing to use Canny to gather and organize user feedback in an
              effort to continuously improve the product experience for their&nbsp;users.
            </p>
            <p className="quote">
              &ldquo;Canny is just customizable enough that I can use it precisely how I prefer
              while keeping it simple for my team and our&nbsp;users.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              We asked Marcus about some of the main benefits he’s noticed from using Canny for
              every stage of a product’s development&nbsp;cycle.
            </p>
            <p>
              Operationally, he mentioned time saved, as well as a greatly reduced
              cognitive&nbsp;load.
            </p>
            <p>
              Faster decision making has also been a huge benefit. Being able to pull up a single
              source of feedback truth in any prioritization conversation got rid of a lot of
              unnecessary back and&nbsp;forth.
            </p>
            <p className="quote">
              &ldquo;We can just pull up users’ feelings about specific features as we’re talking
              about them. It saves a lot of&nbsp;time.&rdquo;
            </p>
            <p>
              Faster and more efficient feature prioritization has had a clear impact on user
              satisfaction.
            </p>
            <p className="quote">
              &ldquo;Canny really helped us to achieve product-market fit for the app during the
              beta&nbsp;period.&rdquo;
            </p>
            <p>
              The main unexpected benefit Marcus mentioned is being able to notify users of when
              something was complete or changed with practically zero&nbsp;effort.
            </p>
            <p>
              Not only has it been useful for time-saving purposes for product managers, it’s also
              helped pull in users who might have become&nbsp;inactive.
            </p>
            <p className="quote">
              &ldquo;It's not just a user communication tool, it’s also a
              re-engagement&nbsp;mechanism.&rdquo;
            </p>
            <p>
              Marcus has also noticed some loyalty and brand benefits to providing a tool
              like&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;Our users really appreciate that we’re listening. Being able to freely post
              their thoughts on the product in a nice user interface is so much better than having
              to send an email into the&nbsp;void.&rdquo;
            </p>
            <p>
              With regard to being on the fence about using a feedback tool, he encourages other
              product managers to just give it a&nbsp;try.
            </p>
            <p className="quote">
              &ldquo;I tell people it’s a game-changer. Just try it out and see
              what&nbsp;happens.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="Axios Wordmark" className="axiosWordmark" src={AxiosWordmark} />
            Axios is a digital media company that aims to get people smarter, faster on
            what&nbsp;matters.
            <a href="https://axios.com/" rel="noopener" target="_blank">
              axios.com
            </a>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2016</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>190</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2022</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>3,600+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default hot(AxiosLanding);
